import { useLayoutEffect, useRef, useState } from 'react';
import { Check, Filter } from 'lucide-react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@clarix/ui';
import { cn } from '@clarix/ui/lib/cn';

export interface RoleFilterOption {
  role: string;
  count: number;
}

export interface RoleFilterBarProps {
  options: RoleFilterOption[];
  /** Active role; `'All'` = no filter. */
  value: string;
  onChange: (role: string) => void;
}

// gap-1.5 between pills (6px), and the width the overflow trigger + its gap
// reserves. Slightly generous so a measured pill is never clipped by rounding.
const GAP = 6;
const OVERFLOW_RESERVE = 40;

/**
 * Single-line role filter. Renders as many pills as fit on ONE line; the rest
 * collapse behind an icon-only funnel that opens a popover listing EVERY role.
 *
 * Role names are variable length, so the fitting count is measured off a hidden
 * mirror row and re-flows on resize — a wrap-based approach would make the bar
 * grow a second line as roles are added, pushing the list below it.
 */
export function RoleFilterBar({ options, value, onChange }: RoleFilterBarProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(options.length);
  const [menuOpen, setMenuOpen] = useState(false);

  // Fit as many pills as the bar allows, measured against the WHOLE bar width
  // (stable — it doesn't change when the overflow button toggles).
  useLayoutEffect(() => {
    const bar = barRef.current;
    const mirror = mirrorRef.current;
    if (!bar || !mirror) return;

    const recompute = () => {
      const available = bar.clientWidth;
      const widths = Array.from(mirror.children).map((el) => (el as HTMLElement).offsetWidth);
      let used = 0;
      let count = 0;
      for (let i = 0; i < widths.length; i += 1) {
        const width = widths[i] ?? 0;
        const gap = count > 0 ? GAP : 0;
        // The funnel is ALWAYS on the row, so every pill — the last one included —
        // has to leave room for it.
        if (used + gap + width + OVERFLOW_RESERVE <= available) {
          used += gap + width;
          count += 1;
        } else {
          break;
        }
      }
      setVisibleCount(count);
    };

    const ro = new ResizeObserver(recompute);
    ro.observe(bar);
    recompute();
    return () => ro.disconnect();
  }, [options]);

  const visibleOptions = options.slice(0, visibleCount);
  const hiddenOptions = options.slice(visibleCount);
  // If the applied filter got pushed into the overflow, mark the funnel so the
  // active filter is never invisible.
  const activeHidden = hiddenOptions.some((o) => o.role === value);

  const renderPill = (opt: RoleFilterOption, mirror = false) => {
    const on = !mirror && value === opt.role;
    return (
      <Button
        key={opt.role}
        type="button"
        variant="secondary"
        size="xs"
        aria-pressed={on}
        tabIndex={mirror ? -1 : undefined}
        onClick={mirror ? undefined : () => onChange(opt.role)}
        className={cn(
          'text-sm [&_svg]:size-3',
          'shrink-0 rounded-full px-3 font-medium',
          on && 'bg-primary/10 text-primary hover:bg-primary/15',
        )}
      >
        {opt.role}
        <span className={cn('tabular-nums', on ? 'text-primary/70' : 'text-gray-500/70')}>
          {opt.count}
        </span>
      </Button>
    );
  };

  return (
    <div ref={barRef} className="relative flex items-center gap-1.5 py-2">
      {/* Visible pills — clipped as a safety net; the measure above already keeps
          them within the bar. */}
      <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
        {visibleOptions.map((opt) => renderPill(opt))}
      </div>

      {/* Funnel + the full checklist. ALWAYS rendered, not just on overflow: it is
          the one place that lists EVERY role with its count, and the row would
          otherwise gain and lose a control as people are reassigned. */}
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="xs"
            aria-label="Filter by role"
            className={cn(
              'text-sm [&_svg]:size-3',
              'relative shrink-0 rounded-full px-2',
              activeHidden && 'bg-primary/10 text-primary hover:bg-primary/15',
            )}
          >
            <Filter className="h-3.5 w-3.5" strokeWidth={1.75} />
            {activeHidden && (
              <span className="bg-primary absolute right-0.5 top-0.5 h-[5px] w-[5px] rounded-full" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={6} className="w-56 rounded-xl p-1">
          <p className="px-2 pb-1 pt-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Filter by role
          </p>
          <div className="max-h-72 overflow-y-auto">
            {options.map((opt) => {
              const on = value === opt.role;
              return (
                <button
                  key={opt.role}
                  type="button"
                  onClick={() => {
                    onChange(opt.role);
                    setMenuOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-[6px] px-2 py-1.5 text-left text-lg transition-colors',
                    on ? 'text-primary' : 'text-gray-700 hover:bg-gray-50',
                  )}
                >
                  <Check
                    className={cn('h-3.5 w-3.5 shrink-0', on ? 'opacity-100' : 'opacity-0')}
                    strokeWidth={2.5}
                  />
                  <span className="min-w-0 flex-1 truncate font-medium">{opt.role}</span>
                  <span
                    className={cn(
                      'shrink-0 text-sm tabular-nums',
                      on ? 'text-primary/70' : 'text-gray-500',
                    )}
                  >
                    {opt.count}
                  </span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {/* Hidden mirror — natural pill widths for the fit calc; out of flow so it
          never affects the bar's own layout. */}
      <div
        ref={mirrorRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 flex w-max items-center gap-1.5"
      >
        {options.map((opt) => renderPill(opt, true))}
      </div>
    </div>
  );
}
